import React from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Stack } from '@mui/material';
import { DiscountTypes } from 'api';
import { FormCard, FormInput } from 'components';
import { PercentOutlined } from '@mui/icons-material';
import DISCOUNTS from 'constants/discounts';
import { dataInitialSelectsOptions } from '../../../fields';
import TiersInputs from './TiersInputs';
import CollectionsInput from '../CollectionsInput';
import ProductsInput from './ProductsInput';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
  initialSelectsOptions:
    | ReturnType<typeof dataInitialSelectsOptions>
    | undefined;
}

const QuantityDiscount: React.FC<Props> = (props: Props): JSX.Element => {
  const { control, watch, setValue, initialSelectsOptions } = props;

  return (
    <FormCard title="Settings">
      <Stack spacing={2}>
        <FormInput
          control={control}
          name="metafields_attributes.0.value.basis"
          inputType="radio"
          label="Discount calculation"
          options={DISCOUNTS.BASIS_OPTIONS}
          helperText="Discounted price will be calculated based on the selected option"
        />

        <FormInput
          control={control}
          name="metafields_attributes.0.value.percent_off_on_collection"
          inputType="number"
          label="Discount percentage"
          InputProps={{
            endAdornment: <PercentOutlined fontSize="small" />,
            inputProps: {
              min: 0,
              max: 100,
            },
          }}
          rules={{
            min: 0,
            max: 100,
          }}
        />

        <CollectionsInput
          name="metafields_attributes.0.value.selectCollection"
          control={control}
          watch={watch}
          initialValue={initialSelectsOptions?.metafieldsCollections}
          onChange={(value) => {
            const ids = value.map((v) => v.id);
            setValue(
              'metafields_attributes.0.value.selectedCollectionIds',
              ids,
            );
          }}
        />

        <ProductsInput
          control={control}
          setValue={setValue}
          initialSelectsOptions={initialSelectsOptions}
          watch={watch}
        />

        <TiersInputs
          control={control}
          name="metafields_attributes.0.value.qty_tiers"
          setValue={setValue}
        />

        <FormInput
          control={control}
          name="metafields_attributes.0.value.no_discount_applicable_tag"
          inputType="input"
          label="Exclusion tag"
          helperText="Products with this tag will not receive any discount"
        />

        <FormInput
          control={control}
          name="metafields_attributes.0.value.mix_and_match"
          inputType="switch"
          label="Mix and match"
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            const { checked } = target;
            setValue('metafields_attributes.0.value.mix_and_match', checked);
            setValue('metafields_attributes.0.value.mixmatch', checked);
          }}
        />
      </Stack>
    </FormCard>
  );
};

export default QuantityDiscount;
